import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { STATSIG_FEATURE_GATES } from '../bootstrap/statsigFeatureGates';
import { getExperiment, getExperimentNoExposure, manuallyLogExposure } from './experimentClient';
import { logError } from '../utils/logger';

export enum EXPERIMENTS {
	NTP_SEARCH_FILTERS = 'ntp_search_filters',
	NTP_SEARCH_FILTERS_3P_CONNECTORS = 'ntp_search_filters_3p_connectors',
	NTP_AGENTS_DROPDOWN = 'rovo_extension_ntp_agents_dropdown',
	DEFAULT_TO_CUSTOM_LINKS = 'rovo_extension_default_to_custom_links',

	NTP_NOTIFICATIONS_BELL = 'rovo-extension-ntp-notifications-bell',
	GOOGLE_SEARCH_TOGGLE = 'rovo_extension_google_search_toggle',

	ROVO_CONTEXT_MENU_HIGHLIGHT_ENTRY = 'rovo-ext_pcm_highlight_entry',
	// Immutable Switcheroo key — intentionally not `rovo-ext_pcm_*`. Do not rename.
	ROVO_CONTEXT_MENU_RIGHT_CLICK_MENU = 'rovo_extension_right_click_menu',
	ROVO_CONTEXT_MENU_SEARCH_BEHAVIOR = 'rovo-ext_pcm_search_behavior',
	ROVO_CONTEXT_MENU_SELECTION_CONTEXT = 'rovo-ext_selection_context',
	MIGRATE_USER_PROFILE_CARD = 'pt_user_profile_card_migration_exp',
	NTP_WHATS_NEXT = 'rovo-ext_ntp_whats_next',
	/**
	 * Carries Rovo product context (Confluence page and editor selection) into the side-panel chat
	 * and relays chat write actions back to the page. `config.product` stays 'rovo' in both cohorts —
	 * the chat never impersonates the host product (see ASIMO-4853).
	 *
	 * Must match `EXT_CONTEXT_BRIDGE_EXPERIMENT` in
	 * `@atlaskit/rovo-triggers/extension-context-bridge/constants`: the platform Host and Client read
	 * the same key with the platform experiment client, so it has to be targeted at both
	 * `rovo-extension_web` and the product app (`confluence`, `jira`) to have any effect. The
	 * platform halves read it without logging exposure; this package owns the single exposure call.
	 *
	 * Write additionally relies on the sibling platform gate `rovo-ext_aifc_enabled` (AIFC write path).
	 * https://switcheroo.atlassian.com/ui/gates/0d43e585-b022-4c36-ae4d-1f40d7b78d05/key/rovo-ext_aifc_enabled
	 */
	EXT_CONTEXT_BRIDGE = 'rovo-ext_context_bridge_exp',
}

export enum PARAMETERS {
	cohort = 'cohort',
	isEnabled = 'enabled',
	isNtpSearchFiltersEnabled = 'is_search_filters_enabled',
	is3pConnectorEnabled = 'is3pConnectorEnabled',
	ntpAgentsDropdownTreatment = 'treatment',
	isSettingsUiRevampEnabled = 'is_settings_ui_revamp_enabled',
	// oxlint-disable-next-line typescript/no-duplicate-enum-values
	isDefaultToCustomLinksEnabled = 'isEnabled',

	isNotificationsEnabled = 'notificationsEnabled',
	// oxlint-disable-next-line typescript/no-duplicate-enum-values
	isGoogleSearchToggleEnabled = 'isEnabled',

	variant = 'variant',
	// oxlint-disable-next-line typescript/no-duplicate-enum-values
	isUserProfileCardMigrationEnabled = 'isEnabled',
	// oxlint-disable-next-line typescript/no-duplicate-enum-values
	isSelectionContextEnabled = 'isEnabled',
	// oxlint-disable-next-line typescript/no-duplicate-enum-values
	isNtpWhatsNextEnabled = 'isEnabled',
	// oxlint-disable-next-line typescript/no-duplicate-enum-values
	isExtContextBridgeEnabled = 'isEnabled',
}

export const activeExperiments = [
	{
		experimentName: EXPERIMENTS.NTP_SEARCH_FILTERS,
		parameterName: PARAMETERS.isNtpSearchFiltersEnabled,
	},
	{
		experimentName: EXPERIMENTS.NTP_SEARCH_FILTERS_3P_CONNECTORS,
		parameterName: PARAMETERS.is3pConnectorEnabled,
	},
	{
		experimentName: EXPERIMENTS.NTP_AGENTS_DROPDOWN,
		parameterName: PARAMETERS.ntpAgentsDropdownTreatment,
	},
	{
		experimentName: EXPERIMENTS.DEFAULT_TO_CUSTOM_LINKS,
		parameterName: PARAMETERS.isDefaultToCustomLinksEnabled,
	},

	{
		experimentName: EXPERIMENTS.NTP_NOTIFICATIONS_BELL,
		parameterName: PARAMETERS.isNotificationsEnabled,
	},
	{
		experimentName: EXPERIMENTS.GOOGLE_SEARCH_TOGGLE,
		parameterName: PARAMETERS.isGoogleSearchToggleEnabled,
	},
	{
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_HIGHLIGHT_ENTRY,
		parameterName: PARAMETERS.variant,
	},
	{
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_RIGHT_CLICK_MENU,
		parameterName: PARAMETERS.variant,
	},
	{
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_SEARCH_BEHAVIOR,
		parameterName: PARAMETERS.variant,
	},
	{
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_SELECTION_CONTEXT,
		parameterName: PARAMETERS.isSelectionContextEnabled,
	},
	{
		experimentName: EXPERIMENTS.MIGRATE_USER_PROFILE_CARD,
		parameterName: PARAMETERS.isUserProfileCardMigrationEnabled,
	},
	{
		experimentName: EXPERIMENTS.NTP_WHATS_NEXT,
		parameterName: PARAMETERS.isNtpWhatsNextEnabled,
	},
	{
		experimentName: EXPERIMENTS.EXT_CONTEXT_BRIDGE,
		parameterName: PARAMETERS.isExtContextBridgeEnabled,
	},
];

export const isNtpSearchFiltersEnabled = (): boolean => {
	return getExperiment({
		experimentName: EXPERIMENTS.NTP_SEARCH_FILTERS,
		parameterName: PARAMETERS.isNtpSearchFiltersEnabled,
		defaultValue: false,
	});
};

export const isNtpSearchFilters3pConnectorsEnabled = (): boolean => {
	return getExperiment({
		experimentName: EXPERIMENTS.NTP_SEARCH_FILTERS_3P_CONNECTORS,
		parameterName: PARAMETERS.is3pConnectorEnabled,
		defaultValue: false,
	});
};

export type NtpAgentsDropdownTreatment = 'control' | 'A';
/**
 * - "control": Feature disabled
 * - "A": Show dropdown on Chat tab
 */
export const getNtpAgentsDropdownTreatment = (): NtpAgentsDropdownTreatment => {
	return getExperiment({
		experimentName: EXPERIMENTS.NTP_AGENTS_DROPDOWN,
		parameterName: PARAMETERS.ntpAgentsDropdownTreatment,
		defaultValue: 'control',
	}) as NtpAgentsDropdownTreatment;
};

// https://switcheroo.atlassian.com/ui/gates/6d883a6f-d1ae-451d-a056-51498e1d36b4/key/rovo_extension_default_to_custom_links
// Uses getExperiment (standard exposure) — fires exposure when Shortcuts component renders.
export const isDefaultToCustomLinksEnabled = (): boolean => {
	return getExperiment({
		experimentName: EXPERIMENTS.DEFAULT_TO_CUSTOM_LINKS,
		parameterName: PARAMETERS.isDefaultToCustomLinksEnabled,
		defaultValue: false,
	});
};

/**
 * Session replay feature gate (Statsig). New recording starts when this gate is enabled — see `SessionReplayInitializer`.
 * While the same Gas browser session id is active, replay is not torn down when the gate becomes false;
 * when Gas mints a new session id, `onAnalyticsWebClientSessionRotation` stops replay if no longer eligible.
 *
 * The experiment `rovo-extension-session-replay` has been replaced by the feature gate
 * `rovo-extension-session-replay-gate`. Use this function which now checks the feature gate instead.
 */
export const isSessionReplayEnabled = (): boolean => {
	return FeatureGates.checkGate(STATSIG_FEATURE_GATES.SESSION_REPLAY);
};

export const isNtpNotificationsBellEnabled = (): boolean => {
	return getExperiment({
		experimentName: EXPERIMENTS.NTP_NOTIFICATIONS_BELL,
		parameterName: PARAMETERS.isNotificationsEnabled,
		defaultValue: false,
	});
};

/**
 * Whether the user should see the Google tab toggle alongside the Search and Chat tabs
 * on the new tab page.
 */
export const isGoogleSearchToggleEnabled = (): boolean => {
	return getExperiment({
		experimentName: EXPERIMENTS.GOOGLE_SEARCH_TOGGLE,
		parameterName: PARAMETERS.isGoogleSearchToggleEnabled,
		defaultValue: false,
	});
};

export const isConsentIntegrationEnabled = (): boolean => {
	return FeatureGates.checkGate(STATSIG_FEATURE_GATES.CONSENT);
};

/**
 * Whether the new user profile card (`UserProfileCardWithTrigger`) should be used for customers.
 *
 * Note: dogfooding is gated separately by the `people-teams_migrate-user-profile-card` feature
 * gate. Call sites should render the new card when the feature gate is on OR this experiment is on.
 */
export const isUserProfileCardMigrationEnabled = (): boolean => {
	return getExperiment({
		experimentName: EXPERIMENTS.MIGRATE_USER_PROFILE_CARD,
		parameterName: PARAMETERS.isUserProfileCardMigrationEnabled,
		defaultValue: false,
	});
};

/**
 * Whether the new tab page renders the "What's next" widget in place of the existing "Recent work"
 * widget.
 */
export const isNtpWhatsNextEnabled = (): boolean => {
	return getExperiment({
		experimentName: EXPERIMENTS.NTP_WHATS_NEXT,
		parameterName: PARAMETERS.isNtpWhatsNextEnabled,
		defaultValue: false,
	});
};

/**
 * Whether the side-panel chat mirrors the host product page's context, and relays chat write
 * actions back to it.
 *
 * Read without exposure because the read happens wherever the side-panel chat renders, including on
 * pages the bridge can never act on. {@link exposeUserToExtContextBridgeExperiment} is the exposure
 * point, called only once the user is on a page the bridge could act on.
 */
export const isExtContextBridgeEnabledNoExposure = (): boolean => {
	const isEnabled = getExperimentNoExposure<boolean>({
		experimentName: EXPERIMENTS.EXT_CONTEXT_BRIDGE,
		parameterName: PARAMETERS.isExtContextBridgeEnabled,
		defaultValue: false,
	});

	return isEnabled === true;
};

/**
 * Logs the one exposure for the context-bridge experiment. Must be called from both cohorts, and
 * only when the bridge could actually act, or the measured population stops being the population
 * the treatment applies to.
 */
export const exposeUserToExtContextBridgeExperiment = (): void => {
	if (!FeatureGates.initializeCompleted()) {
		return;
	}

	try {
		manuallyLogExposure(EXPERIMENTS.EXT_CONTEXT_BRIDGE);
	} catch (error) {
		logError({ error, impact: 'Failed to log extension context bridge experiment exposure' });
	}
};

export type RovoContextMenuHighlightEntryVariant = 'control' | 'A' | 'B' | 'C';
export type RovoContextMenuRightClickVariant = 'control' | 'A' | 'B';
export type RovoContextMenuSearchBehaviorVariant = 'control' | 'A' | 'B';

const isRovoContextMenuHighlightEntryVariant = (
	value: unknown,
): value is RovoContextMenuHighlightEntryVariant =>
	value === 'control' || value === 'A' || value === 'B' || value === 'C';

const isRovoContextMenuRightClickVariant = (
	value: unknown,
): value is RovoContextMenuRightClickVariant =>
	value === 'control' || value === 'A' || value === 'B';

const isRovoContextMenuSearchBehaviorVariant = (
	value: unknown,
): value is RovoContextMenuSearchBehaviorVariant =>
	value === 'control' || value === 'A' || value === 'B';

export const getRovoContextMenuHighlightEntryVariant = (): RovoContextMenuHighlightEntryVariant => {
	const value = getExperiment<RovoContextMenuHighlightEntryVariant>({
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_HIGHLIGHT_ENTRY,
		parameterName: PARAMETERS.variant,
		defaultValue: 'control',
	});
	return isRovoContextMenuHighlightEntryVariant(value) ? value : 'control';
};

/**
 * Never exposes: the only exposure for this experiment is relayed by the extension after a
 * right-click on an eligible page, for every arm (`extension-chrome` `rightClickExposure.ts`).
 */
export const getRovoContextMenuRightClickVariantNoExposure =
	(): RovoContextMenuRightClickVariant => {
		const value = getExperimentNoExposure<RovoContextMenuRightClickVariant>({
			experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_RIGHT_CLICK_MENU,
			parameterName: PARAMETERS.variant,
			defaultValue: 'control',
		});
		return isRovoContextMenuRightClickVariant(value) ? value : 'control';
	};

export const getRovoContextMenuSearchBehaviorVariant = (): RovoContextMenuSearchBehaviorVariant => {
	const value = getExperiment<RovoContextMenuSearchBehaviorVariant>({
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_SEARCH_BEHAVIOR,
		parameterName: PARAMETERS.variant,
		defaultValue: 'control',
	});
	return isRovoContextMenuSearchBehaviorVariant(value) ? value : 'control';
};

export const isRovoContextMenuSelectionContextEnabledNoExposure = (): boolean => {
	const isEnabled = getExperimentNoExposure<boolean>({
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_SELECTION_CONTEXT,
		parameterName: PARAMETERS.isSelectionContextEnabled,
		defaultValue: false,
	});

	return isEnabled === true;
};

export const isAnySelectionContextEnabled = (): boolean => {
	const highlightEntry = getExperimentNoExposure<string>({
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_HIGHLIGHT_ENTRY,
		parameterName: PARAMETERS.variant,
		defaultValue: 'control',
	});
	const rightClickMenu = getExperimentNoExposure<string>({
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_RIGHT_CLICK_MENU,
		parameterName: PARAMETERS.variant,
		defaultValue: 'control',
	});

	return (
		(isRovoContextMenuHighlightEntryVariant(highlightEntry) && highlightEntry !== 'control') ||
		(isRovoContextMenuRightClickVariant(rightClickMenu) && rightClickMenu !== 'control') ||
		isRovoContextMenuSelectionContextEnabledNoExposure()
	);
};

export const exposeUserToSelectionContextExperiment = (): void => {
	if (!FeatureGates.initializeCompleted()) {
		return;
	}

	try {
		manuallyLogExposure(EXPERIMENTS.ROVO_CONTEXT_MENU_SELECTION_CONTEXT);
	} catch (error) {
		logError({ error, impact: 'Failed to log selection context experiment exposure' });
	}
};

export const getAllRovoContextMenuVariants = (): {
	highlightEntry: RovoContextMenuHighlightEntryVariant;
	rightClickMenu: RovoContextMenuRightClickVariant;
	searchBehavior: RovoContextMenuSearchBehaviorVariant;
} => ({
	highlightEntry: getRovoContextMenuHighlightEntryVariant(),
	rightClickMenu: getRovoContextMenuRightClickVariantNoExposure(),
	searchBehavior: getRovoContextMenuSearchBehaviorVariant(),
});

export const deriveIsRovoContextMenuHighlightFabEnabled = (
	variant: RovoContextMenuHighlightEntryVariant,
): boolean => variant === 'A';

export const deriveIsRovoContextMenuHighlightMenuDirect = (
	variant: RovoContextMenuHighlightEntryVariant,
): boolean => variant === 'B' || variant === 'C';

export const deriveIsRovoContextMenuHighlightOneLineMenuEnabled = (
	variant: RovoContextMenuHighlightEntryVariant,
): boolean => variant === 'C';

export const deriveIsRovoContextMenuRightClickFabEnabled = (
	variant: RovoContextMenuRightClickVariant,
): boolean => variant === 'A' || variant === 'B';

export const deriveIsRovoContextMenuRightClickOneLineMenuEnabled = (
	variant: RovoContextMenuRightClickVariant,
): boolean => variant === 'B';

export const deriveIsRovoContextMenuSearchInSidePanelEnabled = (
	variant: RovoContextMenuSearchBehaviorVariant,
): boolean => variant === 'A';

export const deriveIsRovoContextMenuSearchFullPageEnabled = (
	variant: RovoContextMenuSearchBehaviorVariant,
): boolean => variant === 'B';

export const deriveIsRovoContextMenuEnabled = (
	highlightEntry: RovoContextMenuHighlightEntryVariant,
	rightClickMenu: RovoContextMenuRightClickVariant,
	searchBehavior: RovoContextMenuSearchBehaviorVariant,
): boolean =>
	highlightEntry !== 'control' || rightClickMenu !== 'control' || searchBehavior !== 'control';

export const isRovoContextMenuHighlightFabEnabled = (): boolean =>
	deriveIsRovoContextMenuHighlightFabEnabled(getRovoContextMenuHighlightEntryVariant());

export const isRovoContextMenuHighlightMenuDirect = (): boolean =>
	deriveIsRovoContextMenuHighlightMenuDirect(getRovoContextMenuHighlightEntryVariant());

export const isRovoContextMenuHighlightOneLineMenuEnabled = (): boolean =>
	deriveIsRovoContextMenuHighlightOneLineMenuEnabled(getRovoContextMenuHighlightEntryVariant());

export const isRovoContextMenuRightClickFabEnabled = (): boolean =>
	deriveIsRovoContextMenuRightClickFabEnabled(getRovoContextMenuRightClickVariantNoExposure());

export const isRovoContextMenuRightClickOneLineMenuEnabled = (): boolean =>
	deriveIsRovoContextMenuRightClickOneLineMenuEnabled(
		getRovoContextMenuRightClickVariantNoExposure(),
	);

export const isRovoContextMenuSearchInSidePanelEnabled = (): boolean =>
	deriveIsRovoContextMenuSearchInSidePanelEnabled(getRovoContextMenuSearchBehaviorVariant());

export const isRovoContextMenuSearchFullPageEnabled = (): boolean =>
	deriveIsRovoContextMenuSearchFullPageEnabled(getRovoContextMenuSearchBehaviorVariant());

export const isRovoContextMenuEnabled = (): boolean => {
	const { highlightEntry, rightClickMenu, searchBehavior } = getAllRovoContextMenuVariants();
	return deriveIsRovoContextMenuEnabled(highlightEntry, rightClickMenu, searchBehavior);
};

export const isRovoContextMenuEnabledNoExposure = (): boolean => {
	const highlightEntryRaw = getExperimentNoExposure<string>({
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_HIGHLIGHT_ENTRY,
		parameterName: PARAMETERS.variant,
		defaultValue: 'control',
	});
	const rightClickMenuRaw = getExperimentNoExposure<string>({
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_RIGHT_CLICK_MENU,
		parameterName: PARAMETERS.variant,
		defaultValue: 'control',
	});
	const searchBehaviorRaw = getExperimentNoExposure<string>({
		experimentName: EXPERIMENTS.ROVO_CONTEXT_MENU_SEARCH_BEHAVIOR,
		parameterName: PARAMETERS.variant,
		defaultValue: 'control',
	});
	const highlightEntry = isRovoContextMenuHighlightEntryVariant(highlightEntryRaw)
		? highlightEntryRaw
		: 'control';
	const rightClickMenu = isRovoContextMenuRightClickVariant(rightClickMenuRaw)
		? rightClickMenuRaw
		: 'control';
	const searchBehavior = isRovoContextMenuSearchBehaviorVariant(searchBehaviorRaw)
		? searchBehaviorRaw
		: 'control';
	return deriveIsRovoContextMenuEnabled(highlightEntry, rightClickMenu, searchBehavior);
};

export type SiteSettingsReadSource = 'rovoextadminsettings';
